import SidebarStyle from './Sidebar.module.scss';
import Logo from '../Img/logo-1.png';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import HealingIcon from '@mui/icons-material/Healing';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import PaidIcon from '@mui/icons-material/Paid';
import ElderlyIcon from '@mui/icons-material/Elderly';
import LocalPharmacyIcon from '@mui/icons-material/LocalPharmacy';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import DownloadingIcon from '@mui/icons-material/Downloading';
import MedicationIcon from '@mui/icons-material/Medication';
import PaletteIcon from '@mui/icons-material/Palette';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';

import React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';

export default function Sidebar({ onAfterNavigate }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = location;
  const { loggedIn } = useSelector((state) => state.authData);
  const baseMenuItems = useMemo(() => [
    {
      id: 1,
      title: 'Dashboard',
      icon: <DashboardIcon />,
      href: '/dashboard',
      role: ['A', 'SA'],
    },
    {
      id: 11,
      title: 'Clinic',
      icon: <HealingIcon />,
      href: '/clinicPage',
      role: ['SA'],
    },
    {
      id: 7,
      title: 'Calendar',
      icon: <CalendarMonthIcon />,
      href: '/calenderPage',
      role: ['A', 'D', 'SA'],
    },
    {
      id: 9,
      title: 'Doctor Availability',
      icon: <AccessTimeIcon />,
      href: '/docAvailability',
      role: ['D', 'A', 'SA'],
    },
    {
      id: 2,
      title: 'Doctor Specialities',
      icon: <LocalHospitalIcon />,
      href: '/docSpecialities',
      role: ['SA'],
    },
  
    {
      id: 3,
      title: 'Doctor',
      icon: <PeopleAltIcon />,
      href: '/doctors',
      role: ['A', 'SA'],
    },
    {
      id: 4,
      title: 'Patients',
      icon: <ElderlyIcon />,
      href: '/patients',
      role: ['A', 'D', 'SA'],
      size: 40,
    },
    {
      id: 15,
      title: 'Patient PHI',
      icon: <AssignmentIndIcon />,
      href: '/patient-phi',
      role: ['A', 'D', 'SA'],
    },

    {
      id: 6,
      title: 'Appointment',
      icon: <EventAvailableIcon />,
      href: '/appointment',
      role: ['A', 'D', 'SA'],
    },
    {
      id: 13,
      title: 'Additional Services',
      icon: <MedicationIcon />,
      href: '/additionalService',
      role: ['A', 'D', 'SA'],
    },
    {
      id: 8,
      title: 'Expense',
      icon: <PaidIcon />,
      href: '/expensePage',
      role: ['A', 'D', 'SA'],
    },
    {
      id: 8,
      title: 'Medicine',
      icon: <LocalPharmacyIcon />,
      href: '/medicinePage',
      role: ['A', 'D', 'SA'],
    },
    {
      id: 12,
      title: 'Pharmacy Shop',
      icon: <AddShoppingCartIcon />,
      href: '/pharmacyPage',
      role: ['A', 'D', 'SA'],
    },
    {
      id: 8,
      title: 'Import Medicine',
      icon: <DownloadingIcon />,
      href: '/importMedicine',
      role: ['A', 'D', 'SA'],
    },
    {
      id: 14,
      title: 'Theme Settings',
      icon: <PaletteIcon />,
      href: '/theme-settings',
      role: ['A', 'D', 'SA'],
    },
    // {
    //   id: 10,
    //   title: 'Patient Forms',
    //   icon: <ListAltIcon />,
    //   href: '/form',
    //   role: ['A', 'D', 'SA'],
    // },
  ].filter((item) => item.role.includes(loggedIn?.role)), [loggedIn?.role]);

  const [draggingHref, setDraggingHref] = useState('');
  const [dragOverHref, setDragOverHref] = useState('');
  const [orderedMenuItems, setOrderedMenuItems] = useState(baseMenuItems);
  const didDropRef = useRef(false);
  const dropGuardTimerRef = useRef(null);

  const sidebarOrderStorageKey = useMemo(() => {
    const userKey = loggedIn?._id || loggedIn?.id || loggedIn?.email || 'guest';
    return `simplycare.sidebarOrder.${userKey}`;
  }, [loggedIn]);

  useEffect(() => {
    const menuByHref = new Map(baseMenuItems.map((item) => [item.href, item]));

    let savedOrder = [];
    try {
      const raw = localStorage.getItem(sidebarOrderStorageKey);
      savedOrder = raw ? JSON.parse(raw) : [];
    } catch (error) {
      savedOrder = [];
    }

    const orderedFromSaved = (savedOrder || [])
      .map((href) => menuByHref.get(href))
      .filter(Boolean);

    const missingItems = baseMenuItems.filter(
      (item) => !(savedOrder || []).includes(item.href),
    );

    setOrderedMenuItems([...orderedFromSaved, ...missingItems]);
  }, [baseMenuItems, sidebarOrderStorageKey]);

  useEffect(() => {
    if (!orderedMenuItems.length) return;
    const hrefOrder = orderedMenuItems.map((item) => item.href);
    localStorage.setItem(sidebarOrderStorageKey, JSON.stringify(hrefOrder));
  }, [orderedMenuItems, sidebarOrderStorageKey]);

  const isActive = (href) => pathname.startsWith(href);

  const go = (href) => {
    if (didDropRef.current) {
      return;
    }
    navigate(href);
    if (typeof onAfterNavigate === 'function') {
      onAfterNavigate();
    }
  };

  const handleDragStart = (event, href) => {
    didDropRef.current = false;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', href);
    }
    setDraggingHref(href);
  };

  const handleDragOver = (event, href) => {
    event.preventDefault();
    if (dragOverHref !== href) {
      setDragOverHref(href);
    }
  };

  const handleDrop = (event, targetHref) => {
    event.preventDefault();
    const sourceHref = draggingHref || event.dataTransfer?.getData('text/plain') || '';

    if (!sourceHref || sourceHref === targetHref) {
      setDraggingHref('');
      setDragOverHref('');
      return;
    }

    const fromIndex = orderedMenuItems.findIndex((item) => item.href === sourceHref);
    const toIndex = orderedMenuItems.findIndex((item) => item.href === targetHref);

    if (fromIndex < 0 || toIndex < 0) {
      setDraggingHref('');
      setDragOverHref('');
      return;
    }

    const nextOrder = [...orderedMenuItems];
    const [moved] = nextOrder.splice(fromIndex, 1);
    nextOrder.splice(toIndex, 0, moved);

    didDropRef.current = true;
    if (dropGuardTimerRef.current) {
      clearTimeout(dropGuardTimerRef.current);
    }
    dropGuardTimerRef.current = setTimeout(() => {
      didDropRef.current = false;
      dropGuardTimerRef.current = null;
    }, 180);
    setOrderedMenuItems(nextOrder);
    setDraggingHref('');
    setDragOverHref('');
  };

  useEffect(() => {
    return () => {
      if (dropGuardTimerRef.current) {
        clearTimeout(dropGuardTimerRef.current);
      }
    };
  }, []);

  return (
    <aside className={SidebarStyle.sideMainBox}>
      <div className={SidebarStyle.logoWrap}>
        <div className={SidebarStyle.logoBox}>
          <img src={Logo} alt="Logo" />
        </div>
      </div>

      <div className={SidebarStyle.list}>
        {orderedMenuItems.map((item) => {
          const active = isActive(item.href);
          const isDragOver = dragOverHref === item.href && draggingHref && draggingHref !== item.href;

          return (
            <div
              key={`${item.id}-${item.href}`}
              className={`${active ? SidebarStyle.currentlink : SidebarStyle.newlink} ${isDragOver ? SidebarStyle.dragOver : ''}`}
              onClick={() => go(item.href)}
              role="button"
              tabIndex={0}
              draggable
              onDragStart={(event) => handleDragStart(event, item.href)}
              onDragOver={(event) => handleDragOver(event, item.href)}
              onDrop={(event) => handleDrop(event, item.href)}
              onDragEnd={() => {
                setDraggingHref('');
                setDragOverHref('');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  go(item.href);
                }
              }}
            >
              <span className={SidebarStyle.iconWrap}>
                {React.cloneElement(item.icon, {
                  style: {
                    color: active ? 'var(--sidebar-active-text)' : 'var(--sidebar-text)',
                    fontSize: 18,
                  },
                })}
              </span>
              <span className={SidebarStyle.linkLabel}>{item.title}</span>
            </div>
          );
        })}
      </div>
    </aside>
  );
}
